import { makeEnum } from '../Base.js'
import * as ser from '../Common/SerUtil.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { Quantity } from '../Common/Quantity.js'
import { Units } from '../Common/Units.js'
import { valOr } from '../SharedUtils.js'

export let ResultsNodeType = makeEnum({
  VerticalSplit: 'VerticalSplit',
  LineChart: 'LineChart',
  MultiLineChart: 'MultiLineChart',
  StackedBarChart: 'StackedBarChart',
  Sunburst: 'Sunburst',
  PolarPlot: 'PolarPlot',
  SummaryTable: 'SummaryTable',
  MonthHourGrid: 'MonthHourGrid',
  PsychrometricsChart: 'PsychrometricsChart',
})

/*
For graphable calculation results
*/
export class ResultsNode {
  constructor(type, componentName) {
    this.type = type;
    this.componentName = componentName;
    this.data = null;
    this.children = new ComponentArray(ResultsNode, {creationFunc: () => { return new ResultsNode(null, 'NA'); }});

    // NOTE - ResultsNode objects may need to get serialized for communicating
    // Space results from the web-worker. Also used by PDF generation.
    this.serFields = [
      'type',
      'componentName',
      ser.genericObjectField('data', []),
      ser.arrayField('children', () => {
        return new ResultsNode(null, 'NA');
      }),
    ]
  }
}

export class LineChartNode extends ResultsNode {
  constructor(data) {
    super(ResultsNodeType.LineChart, 'LineChart');
    this.data = data;
    this._convertToProjectUnits(this.data);
  }

  _convertToProjectUnits(data) {
    let projectUnits = Quantity.getConvertedUnits(data.units);
    data.values = Quantity.convertArrayToUnits(data.values, data.units, projectUnits);
    data.values = data.values.map((value) => {
      let precision = valOr(data.precision, Quantity.getPrecisionForUnits(projectUnits));
      return Quantity.roundToPrecision(value, precision);
    })
    data.units = projectUnits;
    // Add units to labelY
    data.labelY = `${data.labelY} (${Units.getLabel(projectUnits)})`
  }
}

export class MultiLineChartNode extends ResultsNode {
  constructor(data) {
    super(ResultsNodeType.MultiLineChart, 'MultiLineChart');
    this.data = data;
    this._convertToProjectUnits(this.data);
  }

  _convertToProjectUnits(data) {
    let projectUnits = Quantity.getConvertedUnits(data.units);
    for (let series of data.series) {
      series.values = Quantity.convertArrayToUnits(series.values, data.units, projectUnits);
      series.values = series.values.map((value) => {
        let precision = valOr(data.precision, Quantity.getPrecisionForUnits(projectUnits));
        return Quantity.roundToPrecision(value, precision);
      })
    }
    data.units = projectUnits;
    // Add units to labelY
    data.labelY = `${data.labelY} (${Units.getLabel(projectUnits)})`
  }
}

export class StackedBarChartNode extends ResultsNode {
  constructor(data) {
    super(ResultsNodeType.StackedBarChart, 'StackedBarChart');
    this.data = data;
    this._convertToProjectUnits(this.data);
  }

  _convertToProjectUnits(data) {
    let projectUnits = Quantity.getConvertedUnits(data.units);
    for (let dataset of data.datasets) {
      dataset.data = Quantity.convertArrayToUnits(dataset.data, data.units, projectUnits);
      dataset.data = dataset.data.map((value) => {
        let precision = valOr(data.precision, Quantity.getPrecisionForUnits(projectUnits));
        return Quantity.roundToPrecision(value, precision);
      })
    }
    data.units = projectUnits;
    // Add units to labelY
    data.labelY = `${data.labelY} (${Units.getLabel(projectUnits)})`
  }
}

export class PolarPlotNode extends ResultsNode {
  constructor(data) {
    super(ResultsNodeType.PolarPlot, 'PolarPlot');
    this.data = data;
    // Note - no unit conversion is necessary for polar plots (just latitude/longitude)
  }
}

export class SunburstNode extends ResultsNode {
  constructor(data) {
    super(ResultsNodeType.Sunburst, 'Sunburst');
    
    // Sort the data recursively from largest to smallest
    this._sortDataRecursively(data.data);
    this._convertToProjectUnits(data);

    this.data = data;
  }
  
  _sortDataRecursively(data) {
    // Skip special properties that start with underscore
    const items = [];
    for (const key in data) {
      if (!key.startsWith('_')) {
        items.push({
          key: key,
          value: data[key].value,
          item: data[key]
        });
      }
    }
    
    // Sort items by value in descending order
    items.sort((a, b) => b.value - a.value);
    
    // Create a new sorted object
    const sortedData = {};
    for (const specialKey in data) {
      if (specialKey.startsWith('_')) {
        sortedData[specialKey] = data[specialKey];
      }
    }
    
    // Add sorted items
    for (const item of items) {
      sortedData[item.key] = item.item;
      
      // Recursively sort children if they exist
      if (item.item.children) {
        this._sortDataRecursively(item.item.children);
      }
    }
    
    // Replace the original data with sorted data
    Object.keys(data).forEach(key => {
      delete data[key];
    });
    
    Object.keys(sortedData).forEach(key => {
      data[key] = sortedData[key];
    });
  }

  _convertToProjectUnits(toplevelData) {
    // Convert from original units to the project units, in place
    let originalUnits = toplevelData.units;
    let rootObject = toplevelData.data;

    let newUnits = Quantity.getConvertedUnits(originalUnits);
    toplevelData.units = newUnits;
    let precision = valOr(toplevelData.precision, Quantity.getPrecisionForUnits(newUnits));
    let objStack = [rootObject];
    while (objStack.length > 0) {
      let obj = objStack.pop();
      for (const key in obj) {
        if (key.startsWith('_')) {
          continue;
        }
        let childElem = obj[key];
        // Convert the value
        let convertedValue = Quantity.convertValue(childElem.value, originalUnits, newUnits);
        childElem.value = Quantity.roundToPrecision(convertedValue, precision);
        // Process children (note - children is a dict of child objects)
        if (childElem.children) {
          objStack.push(childElem.children);
        }
      }
    }
  }
  
  static getBreakdownForMonthHour(breakdown, mo, hr) {
    // Return a modified breakdown object where all values (which are assumed to
    // be load matrics) are recursively converted to a single value (the element at the given month+hour).
    // This is done by copying the breakdown object and replacing the values with the single value.
    // This is done recursively for all children.
    let breakdownCopy = {};
    for (const key in breakdown) {
      let childElem = breakdown[key];
      breakdownCopy[key] = {
        label: childElem.label,
        value: childElem.value.get([mo, hr]),
      }
      if (childElem.children) {
        breakdownCopy[key].children = SunburstNode.getBreakdownForMonthHour(childElem.children, mo, hr);
      }
    }
    return breakdownCopy;
  }
}

export class SummaryTableNode {
  constructor(data) {
    this.type = ResultsNodeType.SummaryTable;
    this.componentName = 'SummaryTable';
    this.data = data;
    this._convertToProjectUnits(this.data);
  }

  _convertToProjectUnits(data) {
    for (const row of data.rows) {
      if (typeof row.value === 'number') {
        let units = row.units || Units.None;
        let projectUnits = Quantity.getConvertedUnits(units);
        row.units = projectUnits;
        let precision = valOr(data.precision, Quantity.getPrecisionForUnits(projectUnits));
        let convertedValue = Quantity.convertValue(row.value, units, projectUnits);
        row.value = Quantity.roundToPrecision(convertedValue, precision);
      }
    }
  }
}

export class MonthHourGridNode {
  constructor(data) {
    this.type = ResultsNodeType.MonthHourGrid;
    this.componentName = 'MonthHourGrid';
    this.data = data;
    this._convertToProjectUnits(this.data);
  }

  _convertToProjectUnits(data) {
    let projectUnits = Quantity.getConvertedUnits(data.units);
    let precision = valOr(data.precision, Quantity.getPrecisionForUnits(projectUnits));
    // `gridValues` is an array of arrays of values
    for (const row of data.gridValues) {
      for (let i = 0; i < row.length; i++) {
        let convertedValue = Quantity.convertValue(row[i], data.units, projectUnits);
        row[i] = Quantity.roundToPrecision(convertedValue, precision);
      }
    }
    data.units = projectUnits;
  }
}

export class PsychrometricsChartNode {
  constructor(data) {
    this.type = ResultsNodeType.PsychrometricsChart;
    this.componentName = 'PsychrometricsChart';
    this.data = data;
  }
}
